import type { SubjectStatus } from '@/api/client';

/**
 * The subject status vocabulary: stored values, their labels, the legal
 * transitions and the badge colours.
 *
 * MIRRORS DreamIT-Backend/src/services/subjects/statusGate.ts, which is the
 * source of truth. The front end cannot import across the project boundary,
 * so tests/subjectStatus.test.ts reads that file and asserts the two agree —
 * change one and the suite tells you about the other.
 *
 * WHY NOT humanise.ts. `humanStatus` reads a single STATUS_LABELS map shared
 * by users, sites, studies, binder documents, invoices and background jobs.
 * Two of the labels the client asked for — "Discontinued" for `withdrawn` and
 * "Completed Study" for `completed` — collide there: `completed` is also a
 * study status, a binder document status and a job status, and relabelling it
 * in the shared map would have renamed "Completed" to "Completed Study" on
 * the study portfolio, the dashboard and the eISF search. The subject
 * vocabulary needs its own labels, so it gets its own module.
 *
 * Client, 2026-10-14: "Profile - Subject status - drop down- Screening,
 * Screen Fail, Enrolled, Active, Discontinued, completed study".
 */

/** In the client's order, which is also the lifecycle order. */
export const SUBJECT_STATUSES: readonly SubjectStatus[] = [
  'screening',
  'screened',
  'screen_fail',
  'enrolled',
  'active',
  'withdrawn',
  'completed',
];

/**
 * Two labels differ from the stored value on the client's instruction
 * ("withdrawn → Discontinued, completed → Completed Study - yes"). The stored
 * values are deliberately unchanged — see the backend module for why.
 */
export const SUBJECT_STATUS_LABELS: Record<SubjectStatus, string> = {
  screening: 'Screening',
  screened: 'Screened',
  screen_fail: 'Screen Fail',
  enrolled: 'Enrolled',
  active: 'Active',
  withdrawn: 'Discontinued',
  completed: 'Completed Study',
};

/**
 * Legal moves from each status, mirroring SUBJECT_EDGES on the backend.
 *
 * `screened` is absent from `screening`'s list on purpose: finishing
 * screening runs through the onboarding wizard, which carries the study-lock,
 * DoA, expiration, consent and eligibility gates that POSTing a status would
 * step around. Enrolment IS offered, from `screened` — the backend performs
 * the real enrolment (cap, counters, funnel) on that edge.
 */
export const SUBJECT_EDGES: Record<SubjectStatus, SubjectStatus[]> = {
  screening: ['screen_fail', 'withdrawn'],
  screened: ['enrolled', 'screen_fail', 'withdrawn'],
  screen_fail: [],
  enrolled: ['active', 'withdrawn', 'completed'],
  active: ['withdrawn', 'completed'],
  withdrawn: [],
  completed: [],
};

/**
 * What the menu item says, as an instruction rather than a state name.
 *
 * `active` is annotated because it is normally set FOR you: the backend moves
 * an Enrolled subject to Active when their first visit is conducted (client,
 * 2026-10-15: "Nobody has to remember to move them"). The manual item stays
 * for a subject whose first visit happened outside the system, or before that
 * shipped, so the annotation tells a coordinator they do not usually need it.
 */
export const SUBJECT_TRANSITION_LABELS: Record<SubjectStatus, string> = {
  screening: 'Move to Screening',
  screened: 'Mark screening complete',
  screen_fail: 'Record screen failure',
  enrolled: 'Mark as Enrolled',
  active: 'Mark as Active (usually automatic)',
  withdrawn: 'Discontinue subject',
  completed: 'Mark as Completed Study',
};

export const SUBJECT_STATUS_BADGE_CLASS: Record<SubjectStatus, string> = {
  screening: 'badge-info',
  screened: 'badge-info',
  screen_fail: 'badge-neutral',
  enrolled: 'badge-success',
  active: 'badge-success',
  withdrawn: 'badge-danger',
  completed: 'badge-neutral',
};

/**
 * Label for a subject status. Falls back to the raw value rather than
 * throwing, so a row written before a vocabulary change still renders —
 * `consented` rows existed on production until the 2026-10-15 migration.
 */
export function humanSubjectStatus(status: string | null | undefined): string {
  if (!status) return '—';
  return SUBJECT_STATUS_LABELS[status as SubjectStatus] ?? status;
}

export function subjectStatusBadgeClass(status: string | null | undefined): string {
  if (!status) return 'badge-neutral';
  return SUBJECT_STATUS_BADGE_CLASS[status as SubjectStatus] ?? 'badge-neutral';
}

/** Statuses that mean "enrolled and still on the study" — Enrolled or Active. */
export const ON_STUDY_STATUSES: readonly SubjectStatus[] = ['enrolled', 'active'];

export function isOnStudy(status: string | null | undefined): boolean {
  return !!status && (ON_STUDY_STATUSES as readonly string[]).includes(status);
}
